import React, {
  memo, useCallback,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import './horizontal-tab.scss';
import { ChevronRight } from 'react-feather';
import Button from '../../../button';
import { HorizontalTabTypes } from '../../constants';
import { checkTabBorderLess, getIconColor, getLabelColor } from '../../utils';
import Badge from '../../../badge/badge';
import Icon, { IconColors, IconSizes } from '../../../icon';
import Typography from '../../../typography';

/* eslint-disable react/jsx-props-no-spreading */
const HorizontalTab = ({
  id, type, label, onClick, disabled, childrenList, expanded, selected, hasParent, isGroupedTabs,
  renderIcon, badgeProps, onlySelectedChild, component,
}) => {
  const borderLess = checkTabBorderLess(type, expanded, hasParent);
  const isGroupTab = isGroupedTabs && !expanded && !hasParent;
  const notPillGroupTab = isGroupTab && type !== HorizontalTabTypes.PRIMARY_PILL_TAB;
  const isTextType = type !== HorizontalTabTypes.PRIMARY_PILL_TAB && borderLess && !selected;
  const labelColor = getLabelColor(type, selected, borderLess, hasParent);
  const iconColor = getIconColor(type, selected, isGroupedTabs, hasParent, expanded);
  const classes = {
    disabled,
    selected,
    expanded,
    border: !borderLess,
    'group-tab': notPillGroupTab,
  };

  const handleTabClick = useCallback(() => {
    onClick(id, childrenList, component);
  }, [id, onClick, childrenList, component]);

  const getIcon = useCallback(() => {
    if (!hasParent && isGroupedTabs && type === HorizontalTabTypes.PRIMARY_PILL_TAB
      && childrenList?.length > 0 && !expanded) {
      return <Icon icon={ChevronRight} size={IconSizes.SMALL} color={IconColors.PRIMARY} />;
    }
    return renderIcon({ color: iconColor });
  }, [hasParent, iconColor, expanded, renderIcon, isGroupedTabs]);

  const getLabel = useCallback(() => (
    <Typography
      color={labelColor}
      className="ht-label"
      variant={Typography.Variants.sm}
      fontWeight={Typography.FontWeight.medium}
    >
      {label}
    </Typography>
  ), [label, labelColor]);

  return (
    <div
      onClick={handleTabClick}
      className={clsx('horizontal-tab', type, classes, { text: isTextType })}
    >
      <div className="ht-details">
        {label && getLabel()}
        {getIcon()}
        {badgeProps && <Badge {...badgeProps} />}
      </div>
      {(type === HorizontalTabTypes.TEXT_TAB
        || (type === HorizontalTabTypes.PRIMARY_PILL_TAB)) && !onlySelectedChild && (
        <span className={clsx('ht-selected-line', { selected })} />
      )}
    </div>
  );
};

HorizontalTab.defaultProps = {
  ...Button.defaultProps,
  id: '',
  selected: false,
  expanded: false,
  component: null,
  hasParent: false,
  badgeProps: null,
  childrenList: [],
  isGroupedTabs: false,
  renderIcon: () => {},
  onlySelectedChild: false,
  type: HorizontalTabTypes.TEXT_TAB,
};

HorizontalTab.propTypes = {
  ...Button.propTypes,
  id: PropTypes.string,
  selected: PropTypes.bool,
  expanded: PropTypes.bool,
  hasParent: PropTypes.bool,
  component: PropTypes.node,
  renderIcon: PropTypes.func,
  isGroupedTabs: PropTypes.bool,
  onlySelectedChild: PropTypes.bool,
  badgeProps: PropTypes.shape(Badge.propTypes),
  childrenList: PropTypes.arrayOf(PropTypes.shape({})),
  type: PropTypes.oneOf(Object.values(HorizontalTabTypes)),
};

export default memo(HorizontalTab);
